import {
  FC,
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useState,
} from "react";
import { miniAppApi } from "@shared/api";
import { AppRoutes } from "@shared/constants";
import { useLocation } from "react-router-dom";

const DEBUG_FORCE_STREAK = false;

interface StreakContextType {
  shouldShowStreak: boolean;
  currentStreak: number | null;
  isLoading: boolean;
  debugToggleStreak?: () => void;
}

const StreakContext = createContext<StreakContextType>({
  shouldShowStreak: false,
  currentStreak: null,
  isLoading: true,
});

export const useStreak = () => useContext(StreakContext);

interface StreakProviderProps {
  children: ReactNode;
}

export const StreakProvider: FC<StreakProviderProps> = ({ children }) => {
  const location = useLocation();
  const [shouldShowStreak, setShouldShowStreak] = useState(DEBUG_FORCE_STREAK);
  const { data: streakData, isLoading } =
    miniAppApi.useGetApiProfileDaysStreakQuery();
  const currentStreak = streakData?.data?.current_streak || null;

  const debugToggleStreak = () => {
    setShouldShowStreak((prev) => !prev);
  };

  useEffect(() => {
    if (DEBUG_FORCE_STREAK) {
      setShouldShowStreak(true);
      return;
    }

    if (currentStreak) {
      const lastSeenStreak = localStorage.getItem("lastSeenStreak");

      if (lastSeenStreak !== String(currentStreak)) {
        setShouldShowStreak(true);
        localStorage.setItem("lastSeenStreak", String(currentStreak));
      }
    }
  }, [currentStreak]);

  useEffect(() => {
    if (!DEBUG_FORCE_STREAK && location.pathname === `/${AppRoutes.STREAK}`) {
      setShouldShowStreak(false);
    }
  }, [location.pathname]);

  const contextValue = {
    shouldShowStreak,
    currentStreak,
    isLoading,
    ...(process.env.NODE_ENV === "development" ? { debugToggleStreak } : {}),
  };

  return (
    <StreakContext.Provider value={contextValue}>
      {children}
    </StreakContext.Provider>
  );
};
